import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  Input,
  SimpleChanges,
  OnChanges,
  AfterViewInit
} from '@angular/core';
import { Platform } from '@ionic/angular';
import annotationPlugin from 'chartjs-plugin-annotation';
import {
  BarController,
  BarElement,
  CategoryScale,
  Chart,
  ChartConfiguration,
  ChartDataset,
  ChartEvent,
  ChartType,
  ChartTypeRegistry,
  Legend,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  TimeScale,
  Tooltip,
  TooltipCallbacks,
  TooltipItem,
  TooltipPositionerFunction
} from 'chart.js';
import { _DeepPartialObject } from 'chart.js/dist/types/utils';
import 'chartjs-adapter-date-fns';
import { nb } from 'date-fns/locale';
import { hasChanged } from 'src/app/utils/change-utils';

// TypeScript declaration for custom tooltip positioner
declare module 'chart.js' {
  interface TooltipPositionerMap {
    myCustomPositioner: TooltipPositionerFunction<ChartType>;
  }
}

const intersectDataVerticalLine = {
  id: 'intersectDataVerticalLine',
  beforeDraw: (chart, args, options) => {
    const chartArea = chart.chartArea;
    const ctx = chart.ctx;
    ctx.save();

    // Get the tooltip x position
    const x = chart.tooltip.caretX;

    // grey vertical hover line - full chart height
    ctx.beginPath();
    ctx.moveTo(x, chartArea.top);
    ctx.lineTo(x, chartArea.bottom);
    ctx.lineWidth = 2;
    ctx.strokeStyle = 'rgba(0,0,0, 0.1)';
    ctx.stroke();

    // colored vertical hover line - ['tooltip position' to chart bottom] - only for charts 1 dataset
    if (chart.data.datasets.length === 1) {
      ctx.beginPath();
      ctx.moveTo(x, chart.tooltip.caretY);
      ctx.lineTo(x, chartArea.bottom);
      ctx.lineWidth = 2;
      ctx.strokeStyle = chart.data.datasets[0].borderColor;
      ctx.stroke();
    }
    ctx.restore();
  }
};

@Component({
  selector: 'app-analyse-day-usage-chart',
  standalone: true,
  template: `
    <div class="analyse-day-usage-chart-container">
      <canvas class="mx-a" id="{{ chartId }}"></canvas>
    </div>
  `,
  styleUrls: ['./bar-chart.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AnalyseDayUsageChartComponent implements OnInit, AfterViewInit, OnChanges {
  @Input() chartId: string;
  @Input() dataSets: ChartDataset<'bar' | 'line'>[]; // Tillat miksede datasett
  @Input() labels: Date[] | string[];
  @Input() tooltipCallbacks: _DeepPartialObject<
    TooltipCallbacks<'bar' | 'line'>
  >;
  @Input() showYTitle = false;
  @Input() showLegend = false;
  @Input() yUnit?: 'kWh' | 'kr';
  @Input() yUnitRight?: string;
  @Input() xUnit?: 'day' | 'hour' | 'month' | 'year';
  @Input() annotations?: any;
  @Input() tooltipFilter?: (
    tooltipItem: TooltipItem<keyof ChartTypeRegistry>
  ) => boolean;
  @Input() disableOnClick: boolean = false;
  @Input() stacked: boolean = false;
  @Input() yMax?: number;
  @Input() animationDisabled = false;
  @Input() majorTicks = false;

  chart: Chart;
  isBrowser: boolean;
  axisFontSize: number;
  xAxisProps: any;
  legendProps: any;
  animationProps: any;
  autoSkipTicks = false;
  minValue: number;

  constructor(private platform: Platform) { }

  ngOnInit(): void {
    this.isBrowser =
      !this.platform.is('capacitor') &&
      !this.platform.is('mobileweb') &&
      this.platform.width() >= 1000;
    this.axisFontSize = this.isBrowser ? 14 : 12;

    Chart.register(
      BarController,
      TimeScale,
      CategoryScale,
      BarElement,
      Tooltip,
      Legend,
      // Om man har linje sammen med bar (spot)
      LineController,
      LineElement,
      PointElement,
      LinearScale,
      annotationPlugin
    );

    Chart.defaults.font.family = 'Plus Jakarta Sans';
    Chart.defaults.font.weight = 'bold';
    Chart.defaults.color = '#002987';
    Chart.defaults.datasets.bar.backgroundColor = '#002987';
    Chart.defaults.datasets.bar.borderColor = '#f7f7f7';
    Chart.defaults.datasets.bar.borderWidth = 1;
    Tooltip.positioners.myCustomPositioner = this.customTooltipPositioner;

    if (this.dataSets) {
      this.setBarProps();
      this.createAxisProps();
      this.minValue = Math.min(
        ...this.dataSets.flatMap((x) => x.data as number[])
      );
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      !changes.dataSets?.firstChange &&
      hasChanged([
        changes?.dataSets,
        changes?.labels,
        changes?.chartId,
        changes?.showLegend
      ])
    ) {
      this.minValue = Math.min(
        ...this.dataSets.flatMap((x) => x.data as number[])
      );
      this.createChart();
    }
  }

  ngAfterViewInit() {
    if (this.dataSets) {
      this.createChart();
    }
  }

  createChart() {
    if (this.chart != null) {
      this.chart.destroy();
    }

    const ctx = document.getElementById(this.chartId) as HTMLCanvasElement;
    this.setBarProps();
    this.createAxisProps();
    const maxY =
      this.yMax ??
      Math.max(...this.dataSets.flatMap((x) => x.data as number[]));
    const roundedMaxY = maxY > 10 ? Math.ceil(maxY / 10) * 10 : Math.ceil(maxY);

    if (this.animationDisabled) {
      this.animationProps = {
        animation: false
      };
    }

    this.chart = new Chart(
      ctx,
      this.chartOptions(this.labels, this.dataSets, roundedMaxY)
    );
  }

  chartOptions = (
    labels: Date[] | string[],
    datasets: ChartDataset[],
    maxY: number
  ): ChartConfiguration => {
    return {
      type: 'bar',
      data: {
        labels,
        datasets
      },
      plugins: [intersectDataVerticalLine],
      options: {
        maintainAspectRatio: this.isBrowser,
        onResize: (chart, size) => {
          // Endre tykkelsen på søylene responsivt
          if (
            size.width < 500 &&
            this.isBrowser &&
            this.platform.width() < 1000
          ) {
            this.setBarProps();
          } else if (
            size.width >= 550 &&
            !this.isBrowser &&
            this.platform.width() >= 1000
          ) {
            this.setBarProps();
          }
        },
        plugins: {
          tooltip: {
            backgroundColor: 'rgba(255, 255, 255, 1)',
            titleColor: '#002251',
            bodyColor: '#002251',
            footerColor: '#002251',
            borderColor: '#5e5e61',
            borderWidth: 1,
            cornerRadius: 16,
            mode: 'index',
            caretSize: 4,
            intersect: false,
            displayColors: true,
            position: 'myCustomPositioner',
            titleFont: {
              size: this.isBrowser ? 18 : 16,
              weight: 'bold'
            },
            bodyFont: {
              size: this.isBrowser ? 16 : 14,
              weight: 'normal'
            },
            callbacks: this.tooltipCallbacks,
            filter: this.tooltipFilter
          },
          legend: {
            labels: {
              usePointStyle: true
            },
            position: 'bottom',
            ...this.legendProps
          },
          annotation: {
            annotations: this.annotations
          }
        },
        scales: {
          x: {
            stacked: this.stacked,
            adapters: {
              date: {
                locale: nb
              }
            },
            offset: true,
            grid: {
              display: false
            },
            ticks: {
              autoSkip: this.autoSkipTicks,
              maxTicksLimit: 10,
              font: {
                size: this.axisFontSize
              },
              major: {
                enabled: this.majorTicks
              }
            },
            ...this.xAxisProps
          },
          y: {
            stacked: this.stacked,
            grid: {
              lineWidth: 1.5,
              color: '#d8d8d8'
            },
            // min: this.minValue > 0 ? 0 : Math.round(this.minValue * 1.1),
            min: 0,
            ticks: {
              maxTicksLimit: 10,
              font: {
                size: this.axisFontSize
              }
            },
            suggestedMax: maxY,
            beginAtZero: true,
            title: {
              display: this.showYTitle,
              text: this.yUnit,
              padding: { top: 10, bottom: 0 },
              font: {
                size: 14,
                weight: 'bold'
              }
            }
          },
          "y-axis-meteorology": {
            type: 'linear',
            position: 'right',
            stacked: this.stacked,
            grid: {
              // lineWidth: 1.5,
              // color: '#d8d8d8'
              display: false
            },
            // min: this.minValue > 0 ? 0 : Math.round(this.minValue * 1.1),
            ticks: {
              maxTicksLimit: 10,
              color: '#4d284c',
              font: {
                size: this.axisFontSize,
              }
            },
            // suggestedMax: maxY,
            beginAtZero: true,
            title: {
              display: this.showYTitle,
              text: this.yUnitRight ? this.yUnitRight : false,
              padding: { top: 10, bottom: 0 },
              color: '#4d284c',
              font: {
                size: 14,
                weight: 'bold',
              }
            }
          },
        },
        ...this.animationProps
      }
    };
  };

  createAxisProps() {
    if (this.xUnit === 'day') {
      this.xAxisProps = {
        type: 'time',
        time: {
          unit: 'day',
          minUnit: 'day',
          tooltipFormat: 'd. MMMM',
          displayFormats: {
            day: 'dd'
          }
        }
      };
      this.legendProps = { display: this.showLegend };
      this.autoSkipTicks = true;
    }
    if (this.xUnit === 'hour') {
      this.xAxisProps = {
        type: 'time',
        time: {
          unit: 'hour',
          minUnit: 'hour',
          // Må ha dette formatet for å støtte Safari 12
          tooltipFormat: `yyyy/MM/dd HH:mm:ss`,
          displayFormats: {
            hour: 'HH:mm',
            day: 'dd.MMM'
          }
        }
      };
      this.legendProps = { display: this.showLegend };
      this.autoSkipTicks = true;
    }
    if (this.xUnit === 'month') {
      this.xAxisProps = {};
      this.legendProps =
        this.dataSets.length < 2
          ? {
            onClick: (e: ChartEvent) => {
              // Skal ikke skje noe ved klikk på legend
            },
            display: true
          }
          : {
            // Man skal få lov til å fjerne enkelte dataset i grafen ved å trykke på legend
            display: true
          };
    }
    if (this.xUnit === 'year') {
      this.xAxisProps = {};
      this.legendProps = { display: this.showLegend };
    }
    if (this.disableOnClick) {
      this.legendProps = {
        onClick: (e: ChartEvent) => {
          // Skal ikke skje noe ved klikk på legend
        }
      };
    }
  }

  setBarProps() {
    this.isBrowser =
      !this.platform.is('capacitor') &&
      !this.platform.is('mobileweb') &&
      this.platform.width() >= 1000;
    this.axisFontSize = this.isBrowser ? 14 : 12;
    const borderRadius = this.isBrowser ? 8 : 4;
    Chart.defaults.datasets.bar.borderRadius = borderRadius;
    Chart.defaults.datasets.bar.barThickness = this.getBarThickness();
  }

  customTooltipPositioner: TooltipPositionerFunction<keyof ChartTypeRegistry> =
    function(elements, eventPosition) {
      if (elements == null || elements == undefined || elements.length === 0)
        return false;

      const activeElement = elements[0];
      const yPos = 0;

      return {
        x: activeElement.element.x,
        y: yPos,
        yAlign: 'bottom',
        xAlign: 'center'
      };
    };

  getBarThickness() {
    if (this.platform.width() < 350) return 6;
    if (this.isBrowser && this.dataSets?.length === 1) return 16;
    if (!this.isBrowser && this.dataSets?.length > 1) return 6;
    else return 8;
  }
}
